"use client";
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from "react";
import { DEFAULT_PREFERENCES, readStoredPreferences, writeStoredPreferences, } from "./preferences";
const ThemeContext = createContext(null);
function resolve(mode) {
    if (mode !== "system")
        return mode;
    if (typeof window === "undefined")
        return "light";
    return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
}
export function ThemeProvider({ children, defaultPreferences = DEFAULT_PREFERENCES, }) {
    // Read synchronously on the client so the first render already matches the DOM
    // attributes set by `themeScript`, avoiding a hydration mismatch.
    const [preferences, setPreferences] = useState(() => {
        if (typeof window === "undefined")
            return defaultPreferences;
        try {
            return readStoredPreferences(window.localStorage, defaultPreferences);
        }
        catch {
            return defaultPreferences;
        }
    });
    const [systemDark, setSystemDark] = useState(false);
    useEffect(() => {
        if (typeof window.matchMedia !== "function")
            return;
        const query = window.matchMedia("(prefers-color-scheme: dark)");
        setSystemDark(query.matches);
        const onChange = (event) => setSystemDark(event.matches);
        query.addEventListener("change", onChange);
        return () => query.removeEventListener("change", onChange);
    }, []);
    const resolvedTheme = preferences.mode === "system"
        ? systemDark
            ? "dark"
            : "light"
        : preferences.mode;
    useEffect(() => {
        const root = document.documentElement;
        root.setAttribute("data-theme", resolvedTheme);
        root.setAttribute("data-density", preferences.density);
        root.setAttribute("data-radius", preferences.radius);
        root.style.colorScheme =
            resolvedTheme === "dark" || resolvedTheme === "midnight"
                ? "dark"
                : "light";
        try {
            writeStoredPreferences(window.localStorage, preferences);
        }
        catch {
            // Preference persistence is best-effort; the session still honours the choice.
        }
    }, [preferences, resolvedTheme]);
    const update = useCallback((patch) => setPreferences((current) => ({ ...current, ...patch })), []);
    const toggleMode = useCallback(() => setPreferences((current) => ({
        ...current,
        mode: resolve(current.mode) === "dark" ? "light" : "dark",
    })), []);
    const value = useMemo(() => ({
        ...preferences,
        resolvedTheme,
        setMode: (mode) => update({ mode }),
        setDensity: (density) => update({ density }),
        setRadius: (radius) => update({ radius }),
        toggleMode,
    }), [preferences, resolvedTheme, update, toggleMode]);
    return (_jsx(ThemeContext.Provider, { value: value, children: children }));
}
export function useTheme() {
    const context = useContext(ThemeContext);
    if (context === null)
        throw new Error("useTheme must be used inside <ThemeProvider>");
    return context;
}
//# sourceMappingURL=theme-provider.js.map